import { isRoomJid, type Jid } from "../../jid"
import { xml } from "@xmpp/client"
import {
  NS_ATTENSI_ATTACHMENT,
  NS_ATTENSI_SHARE,
  NS_SID,
} from "../../constants"
import type { Element as XmlEl } from "@xmpp/xml"
import type { AttachmentRef, SharedItemRef } from "../../../types"

export function buildBody(text: string) {
  return xml("body", {}, text)
}

/**
 * The shared-learning-item marker (FAME-237) — a bare reference (type + id),
 * sent beside a `<body>` deep-link fallback so a client that does not understand
 * it still shows an openable link. ejabberd and MAM route it opaquely; the
 * receiver resolves the reference to a card (FAME-453).
 */
export function buildShare(ref: SharedItemRef) {
  return xml("share", {
    xmlns: NS_ATTENSI_SHARE,
    type: ref.type,
    id: ref.id,
  })
}

/** The image marker; sent alone for an image-only message, or beside a caption `<body>`. */
export function buildAttachment(ref: AttachmentRef) {
  return xml("attachment", {
    xmlns: NS_ATTENSI_ATTACHMENT,
    id: ref.id,
    width: String(ref.width),
    height: String(ref.height),
  })
}

export function buildOriginId(id: string) {
  return xml("origin-id", { xmlns: NS_SID, id })
}

/**
 * `type` follows the peer: a room takes groupchat, anything else chat. Costanza
 * authorises against the room's archive or the sender's own accordingly, so a
 * mismatch is refused.
 *
 * Both ids are needed, and they do different jobs. `<origin-id>` (XEP-0359) is
 * stable across sessions — it is what dedupes a message against the copy that
 * comes back, and what a retraction names. The stanza `id` is what a server
 * echoes on an ERROR, so it is the only handle a bounce gives us: drop it and
 * `onBounce` cannot find the message to un-stub or remove, silently.
 */
export function buildMessage(id: string, peer: Jid, ...children: XmlEl[]) {
  return xml(
    "message",
    { to: peer, type: isRoomJid(peer) ? "groupchat" : "chat", id },
    buildOriginId(id),
    ...children
  )
}
